import { useEffect, useRef, useState } from "react";
import { JoystickIcon } from "@/assets/icons/JoystickIcon";
import { cctvService } from "@/services";
import type { PtzPreset } from "@/services/types/cctv.types";
import type { PTZDirection } from "@/hooks/usePTZControl";
import { PTZJoystick } from "./PTZJoystick";

/** 8방위 → 단위 벡터 (pan: 우+, tilt: 상+ — ONVIF continuous 속도 좌표계) */
const DIRECTION_VECTORS: Record<PTZDirection, { pan: number; tilt: number }> = {
  E: { pan: 1, tilt: 0 },
  NE: { pan: 0.707, tilt: 0.707 },
  N: { pan: 0, tilt: 1 },
  NW: { pan: -0.707, tilt: 0.707 },
  W: { pan: -1, tilt: 0 },
  SW: { pan: -0.707, tilt: -0.707 },
  S: { pan: 0, tilt: -1 },
  SE: { pan: 0.707, tilt: -0.707 },
};

/** 원하는 속도(desired)와 마지막 전송(sent)을 수렴시키는 루프 주기(ms) */
const SYNC_INTERVAL_MS = 200;
/** 같은 속도 유지 중 재전송 주기 — 연결 유실 등으로 명령이 증발해도 곧 복구되게 */
const KEEPALIVE_MS = 1000;

/** continuous 속도 벡터 — 전부 0 이면 정지(stop) 의미 */
interface Velocity {
  pan: number;
  tilt: number;
  zoom: number;
}
const ZERO_VEL: Velocity = { pan: 0, tilt: 0, zoom: 0 };
const isZeroVel = (v: Velocity) => v.pan === 0 && v.tilt === 0 && v.zoom === 0;
const sameVel = (a: Velocity, b: Velocity) =>
  a.pan === b.pan && a.tilt === b.tilt && a.zoom === b.zoom;
const round3 = (n: number) => Math.round(n * 1000) / 1000;

interface PTZControlProps {
  /** 제어 대상 CCTV id — POST /cctvs/{id}/ptz */
  cctvId: number;
}

/** FAB + Popover 방식의 카메라 PTZ 제어 패널 (조이스틱·줌, 프리셋은 후속) */
export function PTZControl({ cctvId }: PTZControlProps) {
  const [open, setOpen] = useState(false);
  const [speed, setSpeed] = useState(3);
  const [error, setError] = useState<string | null>(null);
  // 프리셋 — 카메라가 빈 슬롯도 디폴트 이름("Preset N")으로 반환하므로 커스텀 이름만 노출
  const [presets, setPresets] = useState<PtzPreset[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetBusy, setPresetBusy] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);

  // 원하는 속도 — 입력(조이스틱/줌)이 갱신하고, 수렴 루프가 카메라에 반영 (리렌더 없음)
  const desiredRef = useRef<Velocity>(ZERO_VEL);
  // 마지막으로 카메라에 성공 전송한 속도와 시각
  const sentRef = useRef<{ v: Velocity; at: number }>({ v: ZERO_VEL, at: 0 });
  const speedRef = useRef(speed);
  const inFlightRef = useRef(false);

  // 렌더 중 ref 할당 대신 effect 미러링 (usePTZControl 의 optionsRef 와 동일 패턴)
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  // 바깥 클릭 시 닫기
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        fabRef.current &&
        !fabRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [open]);

  /** 조이스틱 입력 → 원하는 pan/tilt 속도. speed(1~5) = 최대 속도의 20~100% */
  function setDesiredPanTilt(direction: PTZDirection | null, distance: number) {
    const cur = desiredRef.current;
    if (!direction || distance <= 0) {
      desiredRef.current = { ...cur, pan: 0, tilt: 0 };
      return;
    }
    const vec = DIRECTION_VECTORS[direction];
    const k = distance * (speedRef.current / 5);
    desiredRef.current = { ...cur, pan: round3(vec.pan * k), tilt: round3(vec.tilt * k) };
  }

  /** 줌 버튼 홀드 — 누르는 동안 해당 속도, 떼면 0 */
  function setDesiredZoom(dir: 0 | 1 | -1) {
    desiredRef.current = {
      ...desiredRef.current,
      zoom: dir === 0 ? 0 : round3(dir * (speedRef.current / 5)),
    };
  }

  // 수렴 루프 — desired 가 바뀌면 continuous/stop 전송, 유지 중엔 keepalive 재전송.
  // stop 이 한 번 실패해도 desired=0 이 유지되는 한 다음 틱에 재시도된다.
  useEffect(() => {
    if (!open) return;
    desiredRef.current = ZERO_VEL;
    sentRef.current = { v: ZERO_VEL, at: 0 };

    const tick = async () => {
      if (inFlightRef.current) return;
      const desired = { ...desiredRef.current };
      const { v: sent, at } = sentRef.current;
      const changed = !sameVel(desired, sent);
      const needKeepalive = !isZeroVel(desired) && Date.now() - at >= KEEPALIVE_MS;
      if (!changed && !needKeepalive) return;

      inFlightRef.current = true;
      try {
        if (isZeroVel(desired)) await cctvService.ptzStop(cctvId);
        else await cctvService.ptzMove(cctvId, desired);
        sentRef.current = { v: desired, at: Date.now() };
        setError(null);
      } catch {
        setError("제어 명령이 실패했습니다. PTZ 접속 정보를 확인해주세요.");
      } finally {
        inFlightRef.current = false;
      }
    };
    const timer = setInterval(tick, SYNC_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      // 패널 닫힘/카메라 전환/언마운트: 카메라가 돌고 있으면 best-effort 정지
      if (!isZeroVel(sentRef.current.v)) cctvService.ptzStop(cctvId).catch(() => {});
      desiredRef.current = ZERO_VEL;
      sentRef.current = { v: ZERO_VEL, at: 0 };
    };
  }, [open, cctvId]);

  /** 카메라 펌웨어(Hikvision ONVIF)가 ASCII 이름만 저장 — 한글 등은 □ 로 깨짐 (실측) */
  const isAsciiName = (s: string) => /^[\x20-\x7E]*$/.test(s);

  /**
   * 사용자 프리셋만 추림.
   * - 디폴트 슬롯명("Preset {token}") = 미설정 슬롯
   * - Hikvision 펌웨어 내장 기능 프리셋(토큰 33~105 대역) = goto 시 위치 이동이 아니라
   *   특수 기능 실행(Remote reboot 포함)이고 삭제도 불가(204 반환하나 잔존) — 노출 금지
   */
  const HIK_SYSTEM_PRESET =
    /^(Auto-flip|Back to origin|Call patrol \d+|Call pattern \d+|Day mode|Night mode|One-touch patrol|Day\/Night Auto Mode|Set manual limits|Save manual limits|Remote reboot|Call OSD menu|Stop a scan|Start (random|frame|auto|tilt|panorama) scan)$/;

  function customPresets(list: PtzPreset[]): PtzPreset[] {
    return list.filter((p) => p.name !== `Preset ${p.token}` && !HIK_SYSTEM_PRESET.test(p.name));
  }

  async function loadPresets() {
    setPresetsLoading(true);
    try {
      setPresets(customPresets(await cctvService.ptzPresets(cctvId)));
      setError(null);
    } catch {
      setError("프리셋 목록을 불러오지 못했습니다.");
    } finally {
      setPresetsLoading(false);
    }
  }

  // 패널 열릴 때 프리셋 로드
  useEffect(() => {
    if (open) loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cctvId]);

  async function handlePresetSave() {
    const name = presetName.trim();
    if (!name || !isAsciiName(name) || presetBusy) return;
    setPresetBusy(true);
    try {
      await cctvService.ptzPresetSave(cctvId, name);
      setPresetName("");
      await loadPresets();
    } catch {
      setError("프리셋 저장에 실패했습니다.");
    } finally {
      setPresetBusy(false);
    }
  }

  async function handlePresetGoto(token: string) {
    if (presetBusy) return;
    setPresetBusy(true);
    try {
      await cctvService.ptzPresetGoto(cctvId, token);
      setError(null);
    } catch {
      setError("프리셋 이동에 실패했습니다.");
    } finally {
      setPresetBusy(false);
    }
  }

  async function handlePresetDelete(token: string) {
    if (presetBusy) return;
    setPresetBusy(true);
    try {
      await cctvService.ptzPresetDelete(cctvId, token);
      await loadPresets();
    } catch {
      setError("프리셋 삭제에 실패했습니다.");
    } finally {
      setPresetBusy(false);
    }
  }

  return (
    <div className="relative">
      {/* Popover 패널 */}
      {open && (
        <div
          ref={panelRef}
          className="absolute bottom-[4.5rem] right-0 w-[280px] rounded-xl bg-[#1A1D27]/90 backdrop-blur-md shadow-[0_4px_24px_rgba(0,0,0,0.5)]"
          style={{ border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {/* 헤더 */}
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 text-xs font-semibold text-white/70">
            <JoystickIcon size={14} />
            카메라 제어
          </div>

          {/* 섹션 1: PTZ 조이스틱 + Zoom */}
          <div className="flex items-center gap-3 border-t border-white/5 px-4 py-4">
            {/* 조이스틱 — 방향키·WASD 동시 지원 (입력 필드 포커스 중엔 훅이 무시) */}
            <div className="flex flex-1 justify-center">
              <PTZJoystick size={88} onDirectionChange={setDesiredPanTilt} />
            </div>

            {/* 우측: Zoom 컨트롤 — 누르는 동안 continuous 줌, 떼면 정지 */}
            <div className="flex flex-col items-center gap-1.5">
              <span className="text-[10px] font-medium text-white/40">ZOOM</span>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md bg-[#252833] text-white/70 transition-colors hover:bg-[#2A2D3A] hover:text-white active:bg-brand/30"
                onPointerDown={() => setDesiredZoom(1)}
                onPointerUp={() => setDesiredZoom(0)}
                onPointerLeave={() => setDesiredZoom(0)}
                onPointerCancel={() => setDesiredZoom(0)}
                title="Zoom In (누르고 있는 동안)"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" d="M12 6v12M6 12h12" />
                </svg>
              </button>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md bg-[#252833] text-white/70 transition-colors hover:bg-[#2A2D3A] hover:text-white active:bg-brand/30"
                onPointerDown={() => setDesiredZoom(-1)}
                onPointerUp={() => setDesiredZoom(0)}
                onPointerLeave={() => setDesiredZoom(0)}
                onPointerCancel={() => setDesiredZoom(0)}
                title="Zoom Out (누르고 있는 동안)"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" d="M6 12h12" />
                </svg>
              </button>
            </div>
          </div>

          {/* 섹션 2: PTZ 속도 */}
          <div className="border-t border-white/5 px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-medium text-white/40">속도</span>
              <span className="text-[10px] font-medium text-brand">{speed}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-white/30">느림</span>
              <input
                type="range"
                min={1}
                max={5}
                step={1}
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
                className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[#252833] accent-brand [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand"
              />
              <span className="text-[9px] text-white/30">빠름</span>
            </div>
          </div>

          {/* 섹션 3: 프리셋 — 저장된 위치로 카메라 이동 */}
          <div className="border-t border-white/5 px-4 py-3">
            <span className="mb-2 block text-[10px] font-medium text-white/40">프리셋</span>
            {presetsLoading ? (
              <div className="py-1 text-[10px] text-white/30">불러오는 중…</div>
            ) : presets.length === 0 ? (
              <div className="py-1 text-[10px] text-white/30">
                저장된 프리셋이 없습니다. 카메라를 원하는 위치로 움직인 뒤 저장하세요.
              </div>
            ) : (
              <div className="mb-2 flex max-h-[96px] flex-wrap gap-1.5 overflow-y-auto">
                {presets.map((p) => (
                  <span key={p.token} className="group/preset relative inline-flex">
                    <button
                      type="button"
                      disabled={presetBusy}
                      onClick={() => handlePresetGoto(p.token)}
                      title={`'${p.name}' 위치로 이동`}
                      className="max-w-[120px] truncate rounded-lg bg-[#252833] px-2.5 py-1.5 pr-6 text-[11px] font-medium text-white/70 transition-colors hover:bg-[#2A2D3A] hover:text-white active:bg-brand/30 disabled:opacity-50"
                    >
                      {p.name}
                    </button>
                    <button
                      type="button"
                      disabled={presetBusy}
                      onClick={() => handlePresetDelete(p.token)}
                      title="프리셋 삭제"
                      aria-label={`'${p.name}' 프리셋 삭제`}
                      className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-white/25 opacity-0 transition-opacity hover:text-red-400 group-hover/preset:opacity-100"
                    >
                      <svg
                        className="h-3 w-3"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* 현재 위치 저장 */}
            <div className="flex gap-1.5">
              <input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handlePresetSave()}
                placeholder="현재 위치 이름 (영문·숫자)"
                className="h-7 min-w-0 flex-1 rounded-md bg-[#252833] px-2 text-[11px] text-white placeholder:text-white/25 outline-none focus:ring-1 focus:ring-brand/60"
              />
              <button
                type="button"
                disabled={!presetName.trim() || !isAsciiName(presetName) || presetBusy}
                onClick={handlePresetSave}
                className="h-7 shrink-0 rounded-md bg-brand/20 px-2.5 text-[11px] font-medium text-brand transition-colors hover:bg-brand/30 disabled:opacity-40"
              >
                저장
              </button>
            </div>
            {presetName.length > 0 && !isAsciiName(presetName) && (
              <div className="mt-1.5 text-[10px] text-amber-400/80">
                카메라가 영문·숫자 이름만 지원합니다. (한글은 깨져 저장됨)
              </div>
            )}
          </div>

          {/* 제어 실패 표시 — app 에 전역 toast 가 없어 패널 내 인라인 */}
          {error && (
            <div className="border-t border-white/5 px-4 py-2.5 text-[10px] leading-relaxed text-red-400">
              {error}
            </div>
          )}
        </div>
      )}

      {/* FAB 버튼 — PTZ 컨트롤 */}
      <div className="group relative flex items-center">
        {/* hover 시 좌측으로 튀어나오는 PTZ 북마크 탭 */}
        <div className="absolute right-[calc(100%-1rem)] flex translate-x-4 items-center opacity-0 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100">
          <div className="flex items-center gap-1.5 rounded-l-full border border-r-0 border-white/30 bg-brand/20 py-1.5 pl-4 pr-6 backdrop-blur-sm">
            <span className="text-xs font-bold tracking-widest text-brand">PTZ</span>
          </div>
        </div>

        <button
          ref={fabRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          style={
            open
              ? undefined
              : {
                  animation: "ptz-glow-pulse 2s ease-in-out infinite",
                }
          }
          className={`relative z-10 flex h-14 w-14 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all duration-200 ${
            open
              ? "bg-brand text-white border border-brand shadow-[0_0_12px_rgba(255,117,0,0.6)]"
              : "bg-brand text-white border border-white/40 backdrop-blur-sm"
          }`}
          title="카메라 PTZ 제어"
        >
          {!open && (
            <>
              <span
                className="absolute inset-0 rounded-full border border-white/60"
                style={{ animation: "ptz-border-ripple 2s ease-out infinite" }}
              />
              <span
                className="absolute inset-0 rounded-full border border-white/30"
                style={{ animation: "ptz-border-ripple2 2s ease-out infinite 0.8s" }}
              />
            </>
          )}
          <span className="relative z-10">
            <JoystickIcon size={24} />
          </span>
        </button>
      </div>
    </div>
  );
}
